/**
 * AgencyBatchesTab.jsx
 * ------------------------------------------------------------------
 * Lists dispatched bulk batches (scan + geo). Auto-polls every 3s
 * while any batch is in `running` status.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Loader2, CheckCircle, AlertCircle, RefreshCw, Search, Sparkles,
} from "lucide-react";

export default function AgencyBatchesTab({ userEmail }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const load = async () => {
    if (!userEmail) return;
    try {
      const r = await axios.get(`${API}/agency/batches`, {
        headers: { "X-User-Email": userEmail },
        params: { limit: 50 },
      });
      setBatches(r.data?.batches || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userEmail]);

  // Auto-poll every 3s while ANY batch is still running.
  useEffect(() => {
    const anyRunning = batches.some(b => b.status === "running");
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (anyRunning) {
      intervalRef.current = setInterval(load, 3000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [batches]);

  return (
    <div className="space-y-3" data-testid="agency-batches-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold">Job log ({batches.length})</h2>
        <button
          onClick={load}
          data-testid="agency-batches-refresh"
          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Obnoviť
        </button>
      </div>

      {loading && (
        <div className="text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítam…</div>
      )}

      {!loading && batches.length === 0 && (
        <div className="p-8 text-center bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="agency-batches-empty">
          <span className="text-gray-500 text-sm">Žiadne batche zatiaľ. Spusti bulk scan alebo GEO v tabe Bulk Run.</span>
        </div>
      )}

      {batches.map((b, i) => (
        <article key={b.id} className="p-4 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid={`agency-batch-row-${i}`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              {b.status === "done"
                ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                : b.status === "failed"
                  ? <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
                  : <Loader2 className="w-4 h-4 text-purple-400 animate-spin shrink-0" />}
              <span className="text-sm font-bold truncate">
                {b.kind === "scan"
                  ? <><Search className="w-3.5 h-3.5 inline mr-1" /> Bulk Scan</>
                  : <><Sparkles className="w-3.5 h-3.5 inline mr-1" /> Bulk GEO</>}
                · {b.total} klientov
              </span>
              <code className="text-[10px] text-gray-500 truncate">{b.id}</code>
            </div>
            <div className="text-xs text-gray-400">
              {b.status === "running"
                ? <span className="text-purple-300">{b.completed} / {b.total}</span>
                : b.status === "done"
                  ? <span className="text-emerald-300">✓ {b.completed} hotovo, {b.failed} zlyhalo</span>
                  : <span className="text-rose-300">✗ {b.error || "Failed"}</span>}
            </div>
          </div>
          <div className="h-1.5 bg-gray-900 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                b.status === "done" ? "bg-emerald-500"
                : b.status === "failed" ? "bg-rose-500"
                : "bg-purple-500"
              }`}
              style={{ width: `${b.total ? Math.max(2, (b.completed / b.total) * 100) : 2}%` }}
            />
          </div>

          {/* Result breakdown */}
          {b.results && b.results.length > 0 && (
            <details className="mt-2">
              <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-white">Per-klient výsledky ({b.results.length})</summary>
              <ul className="mt-2 space-y-1 text-xs">
                {b.results.slice(0, 20).map((res, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2 px-2 py-1 bg-black/40 rounded-sm">
                    <span className="text-gray-400 font-mono text-[10px]">{res.client_id}</span>
                    {res.status === "done"
                      ? <span className="text-emerald-300 text-[10px]">{res.report_id || res.job_id || "OK"} {res.pages ? `· ${res.pages} pages` : ""}</span>
                      : <span className="text-rose-300 text-[10px] truncate max-w-[260px]" title={res.error}>{res.error || "failed"}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </article>
      ))}
    </div>
  );
}
